import * as moment from 'moment';
import * as _ from 'lodash';
import { useMemo } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { DetailReportWrapper } from './styles';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import RankRenderer from '@nkr/views/admin/center/list/common/rank-renderer';
import { IAccountsPayableOpoDetail } from '@nkr/models/index';

interface IProps {
    show: boolean,
    centerAlias: string,
    data: IAccountsPayableOpoDetail[],
    onClose: () => void
}

const Detail = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    }

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const createTopData = (): IAccountsPayableOpoDetail[] => {
        let result = {
            amount: 0,
            opoId: 0,
            recipientId: 0,
            opoAlias: '',
            invoiceDate: '',
            daysOutstanding: 0,
            recipientAlias: '',
            achAccountNumber: '',
            isPaid: false
        };

        //get transportCost Total
        result.amount = props.data.reduce((a, v) => a = a + v.amount, 0);
        result.daysOutstanding = Math.round(_.sumBy(props.data, x => x.daysOutstanding) / props.data.length);

        return [result];
    };

    const columns: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'R',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R</SortableHeaderComponent>,
            pinnedRowCellRendererFramework: (params: ICellRendererParams) => 'Totals:',
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.opoAlias != '') {
                    return (
                        <RankRenderer {...params} />
                    );
                } else {
                    return null;
                }
            },
            width: 50,
            suppressSizeToFit: true,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
            sortable: false
        },

        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient Alias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Alias</SortableHeaderComponent>,
            width: 100,
            suppressSizeToFit: true
        },

        {
            colId: 'daysOutstanding',
            field: 'daysOutstanding',
            headerName: 'Days O/S',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Days<br />O/S</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.opoAlias != '') {
                    return <>{params.value}</>
                } else {
                    return <>~{params.value}</>
                }
            },
            width: 70
        },

        {
            colId: 'amount',
            field: 'amount',
            headerName: 'Amount',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Amount</SortableHeaderComponent>,
            width: 100,
            sortingOrder: ['desc', 'asc'],
            cellRendererFramework: (params: ICellRendererParams) => params.data.amount.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },

        {
            colId: 'opoAlias',
            field: 'opoAlias',
            headerName: 'OPO Alias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>OPO<br />Alias</SortableHeaderComponent>,
            width: 130,
            sortingOrder: ['desc', 'asc']
        },

        {
            colId: 'isPaid',
            field: 'isPaid',
            headerName: 'Paid',
            pinnedRowCellRendererFramework: (params: ICellRendererParams) => '',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Paid</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.data.isPaid ? <span className='label label-success'>Y</span> : '-',
            width: 100,
            sortingOrder: ['desc', 'asc']
        },

        {
            colId: 'invoiceDate',
            field: 'invoiceDate',
            headerName: 'Invoice Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Invoice<br /> Date</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => moment(params.data.invoiceDate).isValid() ? moment(params.data.invoiceDate).format('MM/DD/YYYY') : '--',
            width: 110,
            sortingOrder: ['desc', 'asc']
        },
    ]

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);
    const topData = useMemo(() => createTopData(), [props.data]);

    return (
        <Modal bsSize='lg' backdrop='static' show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>Payable Center: {props.centerAlias}</Modal.Title>
            </Modal.Header>

            <Modal.Body >
                <DetailReportWrapper style={{ height: 750, overflowY: 'auto' }}>
                    <AgGridReact
                        defaultColDef={{
                            headerClass: 'list-header',
                            cellClass: 'list-cell',
                            sortable: true
                        }}
                        columnDefs={columnDefs}
                        rowData={rowData}
                        headerHeight={45}
                        suppressRowClickSelection={false}
                        suppressCellSelection={true}
                        pinnedTopRowData={topData}
                        noRowsOverlayComponentFramework={() => <div>No records found...</div>}
                        onGridReady={onGridReady}
                        onFirstDataRendered={onFirstDataRendered}
                        onSortChanged={(e: SortChangedEvent) => {
                            e.api.refreshCells();
                        }}
                        getRowStyle={(params: any) => {
                            if (params.node.rowPinned) {
                                return { backgroundColor: 'lightblue', fontWeight: 'bold' };
                            }
                        }}
                    />
                </DetailReportWrapper>

            </Modal.Body>
            <Modal.Footer>
                <Button onClick={props.onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default Detail;